<template>
  <div class="admin-layout">
    <AdminHeader
      :title="pageTitle"
      :unread-alerts="0"
      @toggle-sidebar="toggleSidebar"
      @show-alerts="handleShowAnnouncements"
    />

    <div class="admin-body">
      <AdminSidebar :collapsed="sidebarCollapsed" @close="handleSidebarClose" />

      <div v-if="showMask" class="sidebar-mask" @click="handleSidebarClose"></div>

      <div class="admin-content-wrapper" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
        <AdminTabs ref="tabsRef" />
        <div class="admin-content">
          <div class="content-wrapper">
            <RouterView />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import AdminSidebar from '@/components/admin/AdminSidebar.vue'
import AdminTabs from '@/components/admin/AdminTabs.vue'

const route = useRoute()
const router = useRouter()

const sidebarCollapsed = ref(false)
const isMobile = ref(false)
const tabsRef = ref(null)

const showMask = computed(() => isMobile.value && !sidebarCollapsed.value)

const pageTitle = computed(() => {
  if (typeof route.meta?.title === 'string' && route.meta.title.trim()) {
    return route.meta.title
  }
  const titleMap = {
    'admin-dashboard': '仪表盘',
    'admin-users': '用户管理',
    'admin-user-detail': '用户详情',
    'admin-sign-statistics': '签到统计',
    'admin-logs': '日志管理',
    'admin-announcements': '公告中心',
    'admin-config': '配置管理',
    'admin-watch-keys': '卡密配置',
    'admin-agents': '代理管理',
  }
  return titleMap[route.name] || '管理后台'
})

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

const handleSidebarClose = () => {
  if (isMobile.value) {
    sidebarCollapsed.value = true
  }
}

const handleShowAnnouncements = () => {
  router.push({ name: 'admin-announcements' }).catch(() => {})
}

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768
  if (isMobile.value) {
    sidebarCollapsed.value = true
  }
}

onMounted(() => {
  handleResize()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.admin-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background-color: var(--bg-color);
  overflow: hidden;
}

.admin-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.sidebar-mask {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.admin-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-left: 240px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.admin-content-wrapper.sidebar-collapsed {
  margin-left: 64px;
}

.admin-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  background: radial-gradient(circle at 50% 0%, rgba(14, 165, 233, 0.03) 0%, transparent 70%);
  min-height: 0;
}

.content-wrapper::-webkit-scrollbar {
  width: 8px;
}

.content-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.content-wrapper::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.content-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

@media (max-width: 768px) {
  .admin-content-wrapper {
    margin-left: 0 !important;
  }

  .content-wrapper {
    padding: 16px;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .admin-content-wrapper {
    margin-left: 220px;
  }

  .admin-content-wrapper.sidebar-collapsed {
    margin-left: 64px;
  }
}
</style>